import * as React from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  composeDateTimeValue,
  formatDateTimeDisplay,
  getMonthGrid,
  IT_MONTH_NAMES,
  IT_WEEKDAY_SHORT,
  pad2,
  parseDateTimeValue,
} from '../../lib/dateTime';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES = Array.from({ length: 12 }, (_, i) => pad2(i * 5));

function ensureMinuteOption(time) {
  const minute = (time || '12:00').split(':')[1] || '00';
  if (MINUTES.includes(minute)) return MINUTES;
  return [...MINUTES, minute].sort();
}

function DateTimePicker({
  value = '',
  onChange,
  disabled = false,
  /** When true, time can be toggled off (date-only). */
  allowOptionalTime = true,
  /** When false and allowOptionalTime is false, time is always included. */
  placeholder = 'Seleziona data…',
  id,
  className,
}) {
  const parsed = parseDateTimeValue(value);
  const [open, setOpen] = React.useState(false);
  const [viewYear, setViewYear] = React.useState(() => {
    if (parsed.date) return Number(parsed.date.slice(0, 4));
    return new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = React.useState(() => {
    if (parsed.date) return Number(parsed.date.slice(5, 7)) - 1;
    return new Date().getMonth();
  });
  const [draftDate, setDraftDate] = React.useState(parsed.date);
  const [draftTime, setDraftTime] = React.useState(parsed.time || '12:00');
  const [includeTime, setIncludeTime] = React.useState(
    allowOptionalTime ? parsed.hasTime : true
  );

  React.useEffect(() => {
    if (!open) return;
    const next = parseDateTimeValue(value);
    setDraftDate(next.date);
    setDraftTime(next.time || '12:00');
    setIncludeTime(allowOptionalTime ? next.hasTime : true);
    if (next.date) {
      setViewYear(Number(next.date.slice(0, 4)));
      setViewMonth(Number(next.date.slice(5, 7)) - 1);
    } else {
      const now = new Date();
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth());
    }
  }, [open, value, allowOptionalTime]);

  const displayLabel = formatDateTimeDisplay(value);
  const minuteOptions = ensureMinuteOption(draftTime);
  const [draftHour, draftMinute] = (draftTime || '12:00').split(':');
  const cells = getMonthGrid(viewYear, viewMonth);
  const todayKey = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`;
  })();

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const selectDay = (day) => {
    if (!day) return;
    setDraftDate(`${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`);
  };

  const apply = () => {
    if (!draftDate) {
      onChange?.('');
      setOpen(false);
      return;
    }
    const nextInclude = allowOptionalTime ? includeTime : true;
    onChange?.(composeDateTimeValue(draftDate, draftTime, nextInclude));
    setOpen(false);
  };

  const clear = () => {
    onChange?.('');
    setOpen(false);
  };

  return (
    <>
      <div className={cn('flex gap-2', className)}>
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => setOpen(true)}
          className={cn(
            'ba-input flex min-h-[42px] flex-1 items-center gap-2 text-left font-normal normal-case tracking-normal',
            !displayLabel && 'text-[var(--ba-muted)]'
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-brand-orange" aria-hidden />
          <span className="min-w-0 flex-1 truncate capitalize">
            {displayLabel || placeholder}
          </span>
          {parsed.hasTime && (
            <Clock className="h-3.5 w-3.5 shrink-0 text-[var(--ba-muted)]" aria-hidden />
          )}
        </button>
        {value ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            onClick={clear}
            aria-label="Rimuovi data"
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm gap-3 p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle>
              {allowOptionalTime ? 'Seleziona data' : 'Seleziona data e ora'}
            </DialogTitle>
            <DialogDescription>
              {allowOptionalTime
                ? 'L\'ora è opzionale: puoi indicare solo il giorno dell\'evento.'
                : 'Scegli giorno e orario.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--ba-surface-border)] transition-colors hover:bg-[var(--ba-hover-subtle)]"
                onClick={() => shiftMonth(-1)}
                aria-label="Mese precedente"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <p className="text-sm font-semibold">
                {IT_MONTH_NAMES[viewMonth]} {viewYear}
              </p>
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--ba-surface-border)] transition-colors hover:bg-[var(--ba-hover-subtle)]"
                onClick={() => shiftMonth(1)}
                aria-label="Mese successivo"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
              {IT_WEEKDAY_SHORT.map((label) => (
                <div key={label} className="py-1 text-[10px] font-bold uppercase tracking-wide text-[var(--ba-muted)]">
                  {label}
                </div>
              ))}
              {cells.map((day, idx) => {
                if (!day) {
                  return <div key={`empty-${idx}`} className="h-9" />;
                }
                const key = `${viewYear}-${pad2(viewMonth + 1)}-${pad2(day)}`;
                const selected = draftDate === key;
                const isToday = todayKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => selectDay(day)}
                    className={cn(
                      'h-9 rounded-lg text-sm font-medium transition-colors',
                      selected
                        ? 'bg-brand-orange text-white shadow-sm'
                        : isToday
                          ? 'bg-[var(--ba-accent-orange-subtle)] text-brand-orange'
                          : 'hover:bg-[var(--ba-hover-subtle)]'
                    )}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {allowOptionalTime && (
              <label className="flex items-center gap-2 rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2 text-sm dark:bg-white/[0.02]">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brand-orange"
                  checked={includeTime}
                  onChange={(e) => setIncludeTime(e.target.checked)}
                />
                <Clock className="h-4 w-4 text-brand-orange" aria-hidden />
                Includi ora
              </label>
            )}

            {(includeTime || !allowOptionalTime) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="ba-label" htmlFor={`${id || 'dt'}-hour`}>
                    Ora
                  </label>
                  <select
                    id={`${id || 'dt'}-hour`}
                    className="ba-input"
                    value={draftHour}
                    onChange={(e) => setDraftTime(`${e.target.value}:${draftMinute || '00'}`)}
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="ba-label" htmlFor={`${id || 'dt'}-minute`}>
                    Minuti
                  </label>
                  <select
                    id={`${id || 'dt'}-minute`}
                    className="ba-input"
                    value={draftMinute}
                    onChange={(e) => setDraftTime(`${draftHour || '12'}:${e.target.value}`)}
                  >
                    {minuteOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {draftDate && (
              <p className="rounded-lg bg-[var(--ba-accent-orange-subtle)] px-3 py-2 text-center text-sm font-medium capitalize text-brand-orange">
                {formatDateTimeDisplay(
                  composeDateTimeValue(
                    draftDate,
                    draftTime,
                    allowOptionalTime ? includeTime : true
                  )
                )}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button type="button" variant="ghost" onClick={clear}>
              Rimuovi
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Annulla
              </Button>
              <Button type="button" disabled={!draftDate} onClick={apply}>
                Conferma
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export { DateTimePicker };
